import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  Space,
  Typography,
  message,
  Tabs,
  Descriptions,
  Select,
  Spin
} from 'antd';
import {
  MessageOutlined,
  FileTextOutlined,
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import { useAuth } from '../../context/DBContext'; // Adjust the import path
import { functions } from '../../firebase_setup';

const { Title } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

const AdminBusinessManagement = () => {

  const { 
    fetchBusinessUsers, 
    updateBusiness, 
    sendMessage, 
    fetchUserStatus, 
    currentUser, 
    fetchPendingBusinessUsers, 
    approveBusinessUser, 
    denyBusinessUser,
    fetchPendingServiceClaims, 
    approveServiceClaim,      
    denyServiceClaim,     
    fetchServiceById,
    getUserInfo,
    fetchBusinessReportData,
    fetchLocalAuthorityUsers, // Added to fetch local authorities
  } = useAuth();

  // **State Variables**
  const [businesses, setBusinesses] = useState([]);
  const [filteredBusinesses, setFilteredBusinesses] = useState([]);
  const [loadingBusinesses, setLoadingBusinesses] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loadingStates, setLoadingStates] = useState({});
  const [loadingAction, setLoadingAction] = useState(false);
  const [form] = Form.useForm();

  // **Modal States**
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [editingBusiness, setEditingBusiness] = useState(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isMessageModalVisible, setIsMessageModalVisible] = useState(false);
  const [messageContent, setMessageContent] = useState('');
  const [recipient, setRecipient] = useState(null);

  // **Local Authorities**
  const [authorities, setAuthorities] = useState([]);

  // **States for handling modals and editing**
  const [isServiceModalVisible, setIsServiceModalVisible] = useState(false);
  const [selectedServiceClaim, setSelectedServiceClaim] = useState(null);

  // **States for search queries**
  const [pendingSearch, setPendingSearch] = useState('');
  const [approvedSearch, setApprovedSearch] = useState('');
  const [serviceSearch, setServiceSearch] = useState('');

  // **Filtered lists based on approval status and search**
  const [filteredApproved, setFilteredApproved] = useState([]);
  const [filteredServiceClaims, setFilteredServiceClaims] = useState([]);

  const [pendingBusinesses, setPendingBusinesses] = useState([]); // All pending businesses
  const [filteredPending, setFilteredPending] = useState([]); // Filtered pending businesses

  // **State for pagination of pending businesses**
  const [currentPendingPage, setCurrentPendingPage] = useState(1);
  const [pageSizePending, setPageSizePending] = useState(10);

  // **States for Pending Service Approvals**
  const [currentServicePage, setCurrentServicePage] = useState(1);
  const [pageSizeService, setPageSizeService] = useState(10);
  const [loadingServiceClaims, setLoadingServiceClaims] = useState(false);

  // **Service Claims State**
  const [serviceClaims, setServiceClaims] = useState([]);

  // **State for Selected Service Details**
  const [selectedService, setSelectedService] = useState(null);
  const [isServiceDetailModalVisible, setIsServiceDetailModalVisible] = useState(false);

  // **State for loading reports**
  const [loadingReport, setLoadingReport] = useState(false);

  // **Fetch Local Authorities**
  useEffect(() => {
    const fetchAuthorities = async () => {
      try {
        const data = await fetchLocalAuthorityUsers();
        setAuthorities(data);
      } catch (error) {
        console.error('Error fetching local authorities:', error);
      }
    };
    fetchAuthorities();
  }, [fetchLocalAuthorityUsers]);

  // **Fetch Businesses**
  useEffect(() => {
    async function loadBusinesses() {
      try {
        const fetchedBusinesses = await fetchBusinessUsers();
        const convertedBusinesses = fetchedBusinesses.map(business => ({
          ...business,
          createdAt: business.createdAt?.toDate().toString()  // Convert Timestamp to string
        }));
        setBusinesses(convertedBusinesses);
        setFilteredBusinesses(convertedBusinesses);

        // Initialize loading states
        const initialLoadingStates = {};
        convertedBusinesses.forEach(business => {
          initialLoadingStates[business.id] = false;
        });
        setLoadingStates(initialLoadingStates);

      } catch (error) {
        message.error("Failed to load businesses.");
      } finally {
        setLoadingBusinesses(false);
      }
    }
    loadBusinesses();
  }, []);

  // **Filter businesses based on searchValue**
  useEffect(() => {
    const filtered = businesses.filter(business =>
      Object.keys(business).some(key =>
        business[key] && String(business[key]).toLowerCase().includes(searchValue.toLowerCase())
      )
    );
    setFilteredBusinesses(filtered);
  }, [businesses, searchValue]);

  // **Fetch Pending Businesses**
  useEffect(() => {
    async function loadPendingBusinesses() {
      setLoadingBusinesses(true); // Optionally manage a separate loading state for pending businesses
      try {
        const pendingBusinesses = await fetchPendingBusinessUsers();
        const convertedBusinesses = pendingBusinesses.map(business => ({
          ...business,
          createdAt: business.createdAt?.toDate().toString() // Convert Timestamp to string
        }));
        setPendingBusinesses(convertedBusinesses);
        setFilteredPending(convertedBusinesses);
      } catch (error) {
        message.error("Failed to load pending businesses: " + error.message);
      } finally {
        setLoadingBusinesses(false);
      }
    }

    loadPendingBusinesses();
  }, []);

  // **Filter Pending Businesses**
  useEffect(() => {
    const filtered = pendingBusinesses.filter(business => 
      (business.name && business.name.toLowerCase().includes(pendingSearch.toLowerCase())) ||
      (business.email && business.email.toLowerCase().includes(pendingSearch.toLowerCase()))
    );
    setFilteredPending(filtered);
  }, [pendingSearch, pendingBusinesses]);

  // **Fetch Pending Service Claims**
  useEffect(() => {
    async function loadPendingServiceClaims() {
      setLoadingServiceClaims(true);
      try {
        const claims = await fetchPendingServiceClaims();
        setServiceClaims(claims);
        setFilteredServiceClaims(claims);
      } catch (error) {
        message.error("Failed to load pending service claims.");
      } finally {
        setLoadingServiceClaims(false);
      }
    }

    loadPendingServiceClaims();
  }, [fetchPendingServiceClaims]);

  // **Filter Service Claims based on search**
  useEffect(() => {
    const filtered = serviceClaims.filter(sc =>
      sc.businessName.toLowerCase().includes(serviceSearch.toLowerCase()) ||
      sc.serviceName.toLowerCase().includes(serviceSearch.toLowerCase()) ||
      sc.serviceDetails.toLowerCase().includes(serviceSearch.toLowerCase())
    );
    setFilteredServiceClaims(filtered);
  }, [serviceSearch, serviceClaims]);

  // **Pagination for pending businesses**
  useEffect(() => {
    const startIndex = (currentPendingPage - 1) * pageSizePending;
    const endIndex = startIndex + pageSizePending;
    setFilteredPending(pendingBusinesses.slice(startIndex, endIndex));
  }, [pendingBusinesses, currentPendingPage, pageSizePending]);

  // **Pagination for pending service claims**
  useEffect(() => {
    const startIndex = (currentServicePage - 1) * pageSizeService;
    const endIndex = startIndex + pageSizeService;
    setFilteredServiceClaims(serviceClaims.slice(startIndex, endIndex));
  }, [serviceClaims, currentServicePage, pageSizeService]);

  // **Function Definitions**

  // **View Business Details**
  const viewBusiness = (business) => {
    setSelectedBusiness(business);
    setIsViewModalVisible(true);
  };

  const handleViewCancel = () => {
    setIsViewModalVisible(false);
  };

  // **Toggle Business Status**
  const toggleBusinessStatus = async (business) => {
    setLoadingStates(prev => ({ ...prev, [business.id]: true }));
    try {
      const newStatus = !business.active;
      const result = await functions.httpsCallable('toggleUserStatus')({
        userId: business.id,
        disable: business.active,
      });
      if (result.data.success) {
        setBusinesses(prevBusinesses =>
          prevBusinesses.map(b =>
            b.id === business.id ? { ...b, active: newStatus } : b
          )
        );
        message.success(`Business ${newStatus ? 'activated' : 'deactivated'} successfully.`);
      } else {
        message.error(result.data.message);
      }
    } catch (error) {
      message.error('Failed to update business status.');
      console.error('Error toggling business status:', error);
    } finally {
      setLoadingStates(prev => ({ ...prev, [business.id]: false }));
    }
  };

  // **Edit Business**
  const editBusiness = (business) => {
    setEditingBusiness(business);
    form.setFieldsValue(business);
    setIsEditModalVisible(true);
  };

  const handleEditOk = async () => {
    setLoadingAction(true);
    if (editingBusiness) {
      const updatedData = form.getFieldsValue();

      // Update in database
      await updateBusiness(editingBusiness.id, updatedData);

      // Update local state
      setBusinesses(prevBusinesses =>
        prevBusinesses.map(b =>
          b.id === editingBusiness.id ? { ...b, ...updatedData } : b
        )
      );

      message.success('Business details updated successfully.');
      setIsEditModalVisible(false);
      setEditingBusiness(null);
      setLoadingAction(false);
    }
  };

  const handleEditCancel = () => {
    setIsEditModalVisible(false);
  };

  // **Message Business**
  const messageBusiness = (business) => {
    setRecipient(business);
    setIsMessageModalVisible(true);
  };

  const messageAllBusinesses = () => {
    setRecipient(null);
    setIsMessageModalVisible(true);
  };

  const handleSendMessage = async () => {
    setLoadingAction(true);
    if (recipient) {
      try {
        await sendMessage(currentUser.uid, recipient.name, recipient.id, messageContent);
        message.success(`Message sent to ${recipient.name}`);
      } catch (error) {
        message.error('Failed to send message to the business.');
        console.error('Error sending message:', error);
      }
    } else {
      try {
        await Promise.all(
          businesses.map(business =>
            sendMessage(currentUser.uid, business.name, business.id, messageContent)
          )
        );
        message.success('Message sent to all businesses.');
      } catch (error) {
        message.error('Failed to send message to all businesses.');
        console.error('Error sending message:', error);
      }
    }
    setIsMessageModalVisible(false);
    setLoadingAction(false);
  };

  // **Generate Reports**
  const generateBusinessReport = async (business) => {
    message.info(`Generating report for ${business.name}.`);
    setLoadingReport(true);
    try {
      // Fetch business report data
      const reportData = await fetchBusinessReportData(business.id);

      // Prepare CSV data
      const csvRows = [];

      // Business Profile
      csvRows.push(['Business Profile']);
      csvRows.push(['Field', 'Value']);
      csvRows.push(['Business Name', reportData.name || '']);
      csvRows.push(['Email', reportData.email || '']);
      csvRows.push(['Address', reportData.address || '']);
      csvRows.push(['Postcode', reportData.postcode || '']);
      csvRows.push(['Organization Type', reportData.organizationType || '']);
      csvRows.push(['Registration Number', reportData.registrationNumber || '']);
      csvRows.push(['VAT Number', reportData.vatNumber || '']);
      csvRows.push(['Role', reportData.businessRole || '']);
      csvRows.push(['Bank Account Number', reportData.bankAccountNumber || '']);
      csvRows.push(['Sort Code', reportData.sortCode || '']);
      csvRows.push(['Local Authority ID', reportData.localAuthorityId || '']);
      csvRows.push([]);

      // Services
      csvRows.push(['Services']);
      csvRows.push(['Service ID', 'Service Name', 'Category', 'Address', 'Price']);
      reportData.services.forEach((service) => {
        csvRows.push([
          service.id || '',
          service.name || '',
          service.category ? service.category.join(', ') : '',
          service.address || '',
          service.price || '',
        ]);
      });
      csvRows.push([]);

      // Bookings
      csvRows.push(['Bookings']);
      csvRows.push(['Service Name', 'Customer Name', 'Booking Status', 'Timestamp']);
      reportData.bookings.forEach((booking) => {
        csvRows.push([
          booking.serviceName || '',
          booking.customerName || '',
          booking.bookingStatus || '',
          booking.timestamp ? booking.timestamp.toString() : '',
        ]);
      });
      csvRows.push([]);

      // Convert rows to CSV string
      const csvContent = csvRows.map((e) => e.join(',')).join('\n');

      // Create Blob and trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `${business.name}_Report.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.success(`Report generated for ${business.name}`);
    } catch (error) {
      message.error(`Failed to generate report for ${business.name}`);
      console.error('Error generating business report:', error);
    } finally {
      setLoadingReport(false);
    }
  };

  const generateAllBusinessesReport = async () => {
    message.info('Generating report for all businesses.');
    setLoadingReport(true);
    try {
      const allBusinesses = await fetchBusinessUsers();

      // Fetch report data for all businesses concurrently
      const allReports = await Promise.all(
        allBusinesses.map(async (business) => {
          try {
            const reportData = await fetchBusinessReportData(business.id);
            return { business, reportData };
          } catch (error) {
            console.error(`Error fetching report data for business ${business.id}:`, error);
            return null;
          }
        })
      );

      const csvRows = [];

      for (const report of allReports) {
        if (!report) continue;
        const { business, reportData } = report;

        // Business Profile
        csvRows.push(['Business Profile']);
        csvRows.push(['Field', 'Value']);
        csvRows.push(['Business Name', reportData.name || '']);
        csvRows.push(['Email', reportData.email || '']);
        csvRows.push(['Address', reportData.address || '']);
        csvRows.push(['Postcode', reportData.postcode || '']);
        csvRows.push(['Organization Type', reportData.organizationType || '']);
        csvRows.push(['Registration Number', reportData.registrationNumber || '']);
        csvRows.push(['VAT Number', reportData.vatNumber || '']);
        csvRows.push(['Role', reportData.businessRole || '']);
        csvRows.push(['Bank Account Number', reportData.bankAccountNumber || '']);
        csvRows.push(['Sort Code', reportData.sortCode || '']);
        csvRows.push(['Local Authority ID', reportData.localAuthorityId || '']);
        csvRows.push([]);

        // Services
        csvRows.push(['Services']);
        csvRows.push(['Service ID', 'Service Name', 'Category', 'Address', 'Price']);
        reportData.services.forEach((service) => {
          csvRows.push([
            service.id || '',
            service.name || '',
            service.category ? service.category.join(', ') : '',
            service.address || '',
            service.price || '',
          ]);
        });
        csvRows.push([]);

        // Bookings
        csvRows.push(['Bookings']);
        csvRows.push(['Service Name', 'Customer Name', 'Booking Status', 'Timestamp']);
        reportData.bookings.forEach((booking) => {
          csvRows.push([
            booking.serviceName || '',
            booking.customerName || '',
            booking.bookingStatus || '',
            booking.timestamp ? booking.timestamp.toString() : '',
          ]);
        });
        csvRows.push([]);
      }

      // Convert rows to CSV string
      const csvContent = csvRows.map((e) => e.join(',')).join('\n');

      // Create Blob and trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);

      // Create a link and trigger download
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `All_Businesses_Report.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.success('Report generated for all businesses');
    } catch (error) {
      message.error('Failed to generate report for all businesses.');
      console.error('Error generating all businesses report:', error);
    } finally {
      setLoadingReport(false);
    }
  };

  // **Handle Pagination**
  const handlePageChange = (page, newPageSize) => {
    setCurrentPage(page);
    setPageSize(newPageSize);
  };

  // **Approve a Business**
  const approveBusiness = async (businessId) => {
    Modal.confirm({
      title: 'Are you sure?',
      content: 'Do you want to approve this business?',
      okText: 'Yes',
      okType: 'primary',
      cancelText: 'No',
      onOk: async () => {
        setLoadingAction(true);
        try {
          await approveBusinessUser(businessId);
          message.success("Business approved successfully");
          
          // Remove from pending
          setFilteredPending(prev => prev.filter(b => b.id !== businessId));
          
          // Fetch disabled status
          const disabledStatus = await fetchUserStatus(businessId);
          const active = !disabledStatus;
          
          // Find the approved business data
          const approvedBusiness = pendingBusinesses.find(b => b.id === businessId);
          if (approvedBusiness) {
            setBusinesses(prev => [...prev, { ...approvedBusiness, active }]);
            setFilteredBusinesses(prev => [...prev, { ...approvedBusiness, active }]);
          }
        } catch (error) {
          message.error("Failed to approve business: " + error.message);
        } finally {
          setLoadingAction(false);
        }
      }
    });
  };

  // **Deny a Business**
  const denyBusiness = async (businessId) => {
    Modal.confirm({
      title: 'Are you sure?',
      content: 'Do you want to deny this business?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        setLoadingAction(true);
        try {
          await denyBusinessUser(businessId);
          message.success("Business denied");
          
          // Remove from pending
          setFilteredPending(prev => prev.filter(b => b.id !== businessId));
        } catch (error) {
          message.error("Failed to deny business: " + error.message);
        } finally {
          setLoadingAction(false);
        }
      }
    });
  };

  // **Approve Service Claim Action Handler**
  const approveServiceClaimAction = async (claim) => {
    Modal.confirm({
      title: 'Are you sure?',
      content: `Do you want to approve the service claim "${claim.serviceName}" for "${claim.businessName}"?`,
      okText: 'Approve',
      okType: 'primary',
      cancelText: 'Cancel',
      onOk: async () => {
        setLoadingAction(true);
        try {
          await approveServiceClaim(claim.serviceId, claim.businessId);
          // Remove the approved claim from the list
          setServiceClaims(prev => prev.filter(sc => sc.key !== claim.key));
          setFilteredServiceClaims(prev => prev.filter(sc => sc.key !== claim.key));
          message.success('Service claim approved successfully.');
        } catch (error) {
          message.error(`Failed to approve service claim: ${error.message}`);
        } finally {
          setLoadingAction(false);
        }
      },
    });
  };

  // **Deny Service Claim Action Handler**
  const denyServiceClaimAction = async (claim) => {
    Modal.confirm({
      title: 'Are you sure?',
      content: `Do you want to deny the service claim "${claim.serviceName}" for "${claim.businessName}"?`,
      okText: 'Deny',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        setLoadingAction(true);
        try {
          await denyServiceClaim(claim.serviceId, claim.businessId);
          // Remove the denied claim from the list
          setServiceClaims(prev => prev.filter(sc => sc.key !== claim.key));
          setFilteredServiceClaims(prev => prev.filter(sc => sc.key !== claim.key));
          message.success('Service claim denied successfully.');
        } catch (error) {
          message.error(`Failed to deny service claim: ${error.message}`);
        } finally {
          setLoadingAction(false);
        }
      },
    });
  };

  // **View Service Details**
  const viewService = async (serviceId) => {
    try {
      const serviceDetails = await fetchServiceById(serviceId);
      setSelectedService(serviceDetails);
      setIsServiceDetailModalVisible(true);
    } catch (error) {
      message.error("Failed to load service details.");
      console.error("Error fetching service details:", error);
    }
  };

  const handleServiceDetailCancel = () => {
    setIsServiceDetailModalVisible(false);
    setSelectedService(null);
  };

  // **Fetch and Show Business**
  const fetchAndShowBusiness = async (businessId) => {
    try {
      const business = await getUserInfo(businessId);
      if (business) {
        // Convert createdAt to a string if it exists and is a Timestamp
        if (business.createdAt?.toDate) {
          business.createdAt = business.createdAt.toDate().toString();
        }
        setSelectedBusiness(business);
        setIsViewModalVisible(true);
      } else {
        message.error('Business details not found.');
      }
    } catch (error) {
      console.error('Failed to fetch business details:', error);
      message.error('Failed to load business details.');
    }
  };

  // **Open Modals**
  const openBusinessModalForService = (record) => {
    const business = businesses.find((b) => b.key === record.businessKey);
    if (business) {
      setSelectedBusiness(business);
      setIsEditModalVisible(false);
      setIsViewModalVisible(true);
    } else {
      message.error('Business details not found.');
    }
  };

  const openBusinessModal = (record) => {
    setSelectedBusiness(record);
    setIsViewModalVisible(true);
  };

  const openServiceModal = (record) => {
    setSelectedServiceClaim(record);
    setIsServiceModalVisible(true);
  };

  const openEditModal = (business) => {
    setSelectedBusiness(business);
    setIsViewModalVisible(false);
    setIsEditModalVisible(true);
  };

  // **Handle Closing Modals**
  const handleViewOk = () => {
    setIsViewModalVisible(false);
  };

  const handleServiceOk = () => {
    setIsServiceModalVisible(false);
  };

  const handleServiceCancel = () => {
    setIsServiceModalVisible(false);
  };

  
const columns = [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
    sorter: (a, b) => a.name.localeCompare(b.name),
    render: (text, record) => (
      <a onClick={() => viewBusiness(record)}>{text}</a>
    ),
  },
  {
    title: 'Email',
    dataIndex: 'email',
    key: 'email',
  },
  {
    title: 'Role',
    dataIndex: 'businessRole',
    key: 'businessRole',
    sorter: (a, b) => a.businessRole.localeCompare(b.businessRole),
  },
  {
    title: 'Status',
    dataIndex: 'active',
    key: 'active',
    render: (active, record) => (
      <Switch
        checked={active}
        onChange={() => toggleBusinessStatus(record)}
        checkedChildren="Active"
        unCheckedChildren="Inactive"
        loading={loadingStates[record.id]}  // Use specific loading state
        />
    ),
    sorter: (a, b) => a.active - b.active,
  },
  {
    title: 'Actions',
    key: 'actions',
    render: (_, record) => (
      <Space size="middle">
        <Button icon={<MessageOutlined />} onClick={() => messageBusiness(record)}>
          Message
        </Button>
        <Button icon={<FileTextOutlined />} onClick={() => generateBusinessReport(record)}>
          Report
        </Button>
        <Button type="primary" icon={<EditOutlined />} onClick={() => editBusiness(record)}>
          Edit
        </Button>
      </Space>
    ),
  },
];

  
const pendingColumns = [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
    render: (text, record) => (
      <a onClick={() => viewBusiness(record)}>{text}</a>
    ),      
    sorter: (a, b) => a.name.localeCompare(b.name),
  },
  {
    title: 'Email',
    dataIndex: 'email',
    key: 'email',
    sorter: (a, b) => a.businessRole.localeCompare(b.businessRole),

  },
  {
    title: 'Location',
    dataIndex: 'location',
    key: 'location',

  },
  {
    title: 'Role',
    dataIndex: 'businessRole', // Changed from 'role' to 'businessRole'
    key: 'businessRole',

  },
 
  {
    title: 'Actions',
    key: 'actions',
    width: 200,

    render: (_, record) => (
      <Space size="middle">
        <Button 
          onClick={() => approveBusiness(record.id)} 
          type="primary" 
          icon={<CheckOutlined />}
          size="small"

        >
          Approve
        </Button>
        <Button 
          onClick={() => denyBusiness(record.id)} 
          type="danger" 
          icon={<CloseOutlined />}
          size="small"

        >
          Deny
        </Button>
      </Space>
    ),
  },
];




// **Define Columns for Pending Service Approvals Tab**// Define Columns for Pending Service Approvals Tab
const serviceClaimsColumns = [
  {
    title: 'Business Name',
    dataIndex: 'businessName',
    key: 'businessName',
    render: (text, record) => (
      <a onClick={() => fetchAndShowBusiness(record.businessId)}>{text}</a>  // Assuming businessId is part of record

       
    ),
    width: 200,
  },
  {
    title: 'Service Name',
    dataIndex: 'serviceName',
    key: 'serviceName',
    render: (text, record) => {
      // console.log('Record Data:', record);  // Log the entire record to see available fields
      return (
        <a onClick={() => viewService(record.serviceId)}>{text}</a>
      );
    },
    width: 200,
  },
  {
    title: 'Claimed At',
    dataIndex: 'claimDate',
    key: 'claimDate',
    sorter: (a, b) => new Date(a.claimDate) - new Date(b.claimDate),
    width: 180,
  },
  {
    title: 'Actions',
    key: 'actions',
    width: 200,
    render: (_, record) => (
      <Space size="middle">
        <Button
          type="primary"
          icon={<CheckOutlined />}
          onClick={() => approveServiceClaimAction(record)}
          size="small"
        >
          Approve
        </Button>
        <Button
          type="danger"
          icon={<CloseOutlined />}
          onClick={() => denyServiceClaimAction(record)}
          size="small"
        >
          Deny
        </Button>
      </Space>
    ),
  },
];


    if (loadingBusinesses || loadingServiceClaims ) {

    // Display a loading spinner while the page data is loading
    return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
                      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1600px', margin: '0 auto' }}>
      <Title level={3}>Business Management</Title>
      <Tabs defaultActiveKey="1" type="card">
        {/* Business Tab */}
        <TabPane tab="Business" key="1">
          <Space
            style={{
              marginBottom: 16,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <Input
              placeholder="Search businesses"
              onChange={(e) => setSearchValue(e.target.value)}
              style={{ height: '38px', lineHeight: '38px', width: '600px' }}
            />
            <Button
              icon={<MessageOutlined />}
              onClick={messageAllBusinesses}
            >
              Message All Businesses
            </Button>
            <Button
              icon={<FileTextOutlined />}
              onClick={generateAllBusinessesReport}
            >
              Generate Reports for All
            </Button>
          </Space>
          <Table
            columns={columns}
            dataSource={filteredBusinesses}
            rowKey="id"
            pagination={{
              position: ['bottomCenter'],
              current: currentPage,
              pageSize: pageSize,
              total: filteredBusinesses.length,
              showSizeChanger: true,
              pageSizeOptions: ['10', '30', '50', '100'],
              onChange: handlePageChange,
            }}
            style={{ cursor: 'default' }}
            scroll={{ x: 'auto' }}
          />
        </TabPane>

        {/* Keep the other two tabs unchanged */}
        <TabPane tab="Pending Business Approvals" key="2">
          <Space
            style={{
              marginBottom: 16,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
            size="middle"
          >
            <Input
              placeholder="Search pending businesses"
              onChange={(e) => setPendingSearch(e.target.value)}
              style={{ width: '600px', height: '38px', marginRight: '8px' }}
            />
          </Space>
          <Table
           columns={pendingColumns}
           dataSource={filteredPending}
           rowKey="id"
           pagination={{
            position: ['bottomCenter'], // Centers pagination at the bottom

             current: currentPendingPage,
             pageSize: pageSizePending,
             total: pendingBusinesses.length,
             showSizeChanger: true,
             pageSizeOptions: ['10', '30', '50', '100'],
             onChange: (page, newPageSize) => {
               setCurrentPendingPage(page);
               setPageSizePending(newPageSize);
             },
             onShowSizeChange: (current, size) => {
               setPageSizePending(size);
             }
           }}
           style={{ cursor: 'default' }}
           scroll={{ x: 'auto' }}
          />
        </TabPane>

        <TabPane tab="Pending Service Approvals" key="3">
          <Space
            style={{
              marginBottom: 16,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
            size="middle"
          >
             <Input
              placeholder="Search service claims"
              onChange={(e) => setServiceSearch(e.target.value)}
              style={{ width: '600px', height: '38px', marginRight: '8px' }}
            />
          </Space>
          <Table
  columns={serviceClaimsColumns}
  dataSource={filteredServiceClaims}
  rowKey="key" // Ensure 'key' exists in your data
  pagination={{
    position: ['bottomCenter'],
    current: currentServicePage,
    pageSize: pageSizeService,
    total: filteredServiceClaims.length,
    showSizeChanger: true,
    pageSizeOptions: ['10', '30', '50', '100'],
    onChange: (page, newPageSize) => {
      setCurrentServicePage(page);
      setPageSizeService(newPageSize);
    },
  }}
  style={{ cursor: 'default' }}
  scroll={{ x: 'auto' }}
/>

        </TabPane>
      </Tabs>

      {/* View Business Modal */}
      <Modal
        title="Business Details"
        visible={isViewModalVisible}
        onCancel={handleViewCancel}
        footer={[
          <Button key="close" onClick={handleViewCancel}>
            Close
          </Button>,
        ]}
        width={800}
      >
        {selectedBusiness && (
          <Descriptions
            bordered
            column={1}
            size="small"
          >
            <Descriptions.Item label="Business Name">
              {selectedBusiness.name}
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              {selectedBusiness.email}
            </Descriptions.Item>
            <Descriptions.Item label="Address">
              {selectedBusiness.address}
            </Descriptions.Item>
            <Descriptions.Item label="Postcode">
              {selectedBusiness.postcode}
            </Descriptions.Item>
            <Descriptions.Item label="Organization Type">
              {selectedBusiness.organizationType}
            </Descriptions.Item>
            <Descriptions.Item label="Registration Number">
              {selectedBusiness.registrationNumber}
            </Descriptions.Item>
            <Descriptions.Item label="VAT Number">
              {selectedBusiness.vatNumber}
            </Descriptions.Item>
            <Descriptions.Item label="Role">
              {selectedBusiness.businessRole}
            </Descriptions.Item>
            <Descriptions.Item label="Bank Account Number">
              {selectedBusiness.bankAccountNumber}
            </Descriptions.Item>
            <Descriptions.Item label="Sort Code">
              {selectedBusiness.sortCode}
            </Descriptions.Item>
            <Descriptions.Item label="Documents">
              {selectedBusiness.documents && selectedBusiness.documents.map((doc, index) => (
                <div key={index}>
                  <a href={doc} style={{ color: '#1890ff', fontWeight: 'bold' }} target="_blank" rel="noopener noreferrer">
                    Document {index + 1}
                  </a>
                </div>
              ))}
            </Descriptions.Item>
            <Descriptions.Item label="Local Authority">
              {selectedBusiness.localAuthorityId ? authorities.find(auth => auth.id === selectedBusiness.localAuthorityId)?.name || 'N/A' : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Created At">
              {selectedBusiness.createdAt}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              {selectedBusiness.active ? 'Active' : 'Inactive'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* Edit Business Modal */}
      <Modal
        title="Edit Business Details"
        visible={isEditModalVisible}
        onOk={handleEditOk}
        onCancel={handleEditCancel}
        okText="Save"
        confirmLoading={loadingAction}
        width={800}
      >
        {editingBusiness && (
          <Form layout="vertical" form={form} initialValues={editingBusiness}>
            <Form.Item label="Business Name" name="name">
              <Input />
            </Form.Item>
            <Form.Item label="Address" name="address">
              <Input />
            </Form.Item>
            <Form.Item label="Postcode" name="postcode">
              <Input />
            </Form.Item>
            <Form.Item label="Organization Type" name="organizationType">
              <Select>
                <Option value="Limited Company">Limited Company</Option>
                <Option value="Charity">Charity</Option>
                <Option value="Community Interest Company (CIC)">Community Interest Company (CIC)</Option>
                <Option value="Sole Trader">Sole Trader</Option>
                <Option value="Limited Liability Partnership">Limited Liability Partnership</Option>
                <Option value="Other">Other (please state)</Option>
              </Select>
            </Form.Item>
            <Form.Item label="Registration Number" name="registrationNumber">
              <Input />
            </Form.Item>
            <Form.Item label="VAT Number" name="vatNumber">
              <Input />
            </Form.Item>
            <Form.Item label="Role" name="businessRole">
              <Select>
                <Option value="Owner">Owner</Option>
                <Option value="Manager">Manager</Option>
                <Option value="Staff">Staff</Option>
              </Select>
            </Form.Item>
            <Form.Item label="Bank Account Number" name="bankAccountNumber">
              <Input />
            </Form.Item>
            <Form.Item label="Sort Code" name="sortCode">
              <Input />
            </Form.Item>
            {/* Local Authority */}
            <Form.Item label="Local Authority" name="localAuthorityId">
              <Select>
                <Option value={null}>None</Option>
                {authorities.map((authority) => (
                  <Option key={authority.id} value={authority.id}>
                    {authority.name || `${authority.firstName} ${authority.lastName}`}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* Message Modal */}
      <Modal
        title={recipient ? `Send Message to ${recipient.name}` : 'Send Message to All Businesses'}
        visible={isMessageModalVisible}
        onOk={handleSendMessage}
        onCancel={() => setIsMessageModalVisible(false)}
        okText="Send"
        confirmLoading={loadingAction}
      >
        <Form layout="vertical">
          <Form.Item label="Message">
            <Input.TextArea
              rows={4}
              value={messageContent}
              onChange={(e) => setMessageContent(e.target.value)}
            />
          </Form.Item>
        </Form>
      </Modal>
      

    

    {/* Service Claim Details Modal */}

    <Modal
    title="Service Details"
    visible={isServiceDetailModalVisible}
    onCancel={() => setIsServiceDetailModalVisible(false)}
    footer={[
        <Button key="back" onClick={() => setIsServiceDetailModalVisible(false)}>
            Close
        </Button>
    ]}
    width={800}
>
    {selectedService && (
        <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Name">{selectedService.name}</Descriptions.Item>
            <Descriptions.Item label="Address">{selectedService.address}</Descriptions.Item>
            <Descriptions.Item label="Description">{selectedService.description}</Descriptions.Item>
            <Descriptions.Item label="Email">{selectedService.email}</Descriptions.Item>
            <Descriptions.Item label="Phone Number">{selectedService.phone_number}</Descriptions.Item>
            <Descriptions.Item label="Website">
                <a href={selectedService.website} style={{ color: '#1890ff', fontWeight: 'bold' }} target="_blank" rel="noopener noreferrer">
                {selectedService.website}
                </a>
            </Descriptions.Item>
        </Descriptions>
    )}
</Modal>

    </div>
  );
};

export default AdminBusinessManagement;
